// O que cada campanha estava tentando conseguir — e quanto custou consegui-lo.
//
// A tela do Facebook mostra gasto, impressão e CTR para tudo igual, e deixa
// você garimpar o número que importa dentro de "actions". Aqui a métrica sai
// do objetivo da campanha: uma campanha de mensagem é julgada por conversa
// iniciada, uma de lead por lead, uma de venda por compra. É a diferença
// entre "gastou R$ 62" e "cada conversa custou R$ 10,35".

export const RESULTADOS = {
  CONVERSATIONS: {
    rotulo: "conversas",
    singular: "conversa",
    valor: (m) => m?.funnel?.messagingConversationsStarted || 0
  },
  LEAD_GENERATION: {
    rotulo: "leads",
    singular: "lead",
    valor: (m) => m?.funnel?.leads || 0
  },
  OFFSITE_CONVERSIONS: {
    rotulo: "conversões",
    singular: "conversão",
    valor: (m) => m?.conversions || 0
  },
  LINK_CLICKS: {
    rotulo: "cliques no link",
    singular: "clique",
    valor: (m) => m?.engagementBreakdown?.linkClicks || m?.clicks || 0
  },
  LANDING_PAGE_VIEWS: {
    rotulo: "visitas",
    singular: "visita",
    valor: (m) => m?.funnel?.landingPageViews || 0
  },
  POST_ENGAGEMENT: {
    rotulo: "interações",
    singular: "interação",
    valor: (m) => m?.engagementBreakdown?.postEngagement || m?.engagement || 0
  },
  THRUPLAY: {
    rotulo: "vídeos assistidos",
    singular: "vídeo assistido",
    valor: (m) => m?.videoThruplayWatchedActions || 0
  },
  REACH: {
    rotulo: "alcance",
    singular: "pessoa alcançada",
    valor: (m) => m?.reach || 0
  }
};

// Sem objetivo conhecido, cai no que quase toda campanha tem: clique.
const PADRAO = RESULTADOS.LINK_CLICKS;

export const resultadoDe = (no) => {
  const definicao = RESULTADOS[no?.optimizationGoal] || PADRAO;
  const quantidade = definicao.valor(no?.metrics) || 0;
  const gasto = no?.metrics?.spend || 0;

  return {
    rotulo: quantidade === 1 ? definicao.singular : definicao.rotulo,
    quantidade,
    // Sem resultado não existe custo por resultado — dividir por zero daria
    // "Infinity" na tela, que é pior que não mostrar nada.
    custo: quantidade > 0 ? gasto / quantidade : null
  };
};

// Soma as campanhas visíveis. É o que responde "quanto isso está custando"
// sem abrir uma a uma.
export const somar = (campanhas) => {
  const total = campanhas.reduce(
    (acc, campanha) => {
      const m = campanha.metrics || {};
      const resultado = resultadoDe(campanha);

      return {
        gasto: acc.gasto + (m.spend || 0),
        impressoes: acc.impressoes + (m.impressions || 0),
        cliques: acc.cliques + (m.clicks || 0),
        resultados: acc.resultados + resultado.quantidade,
        conversas:
          acc.conversas + (m.funnel?.messagingConversationsStarted || 0)
      };
    },
    { gasto: 0, impressoes: 0, cliques: 0, resultados: 0, conversas: 0 }
  );

  return {
    ...total,
    ctr: total.impressoes > 0 ? (total.cliques / total.impressoes) * 100 : 0,
    custoPorConversa:
      total.conversas > 0 ? total.gasto / total.conversas : null,
    moeda: campanhas[0]?.currency || "BRL"
  };
};

// A média de CTR ponderada pelas impressões — é contra ela que cada campanha
// é comparada. Média simples deixaria uma campanha de 10 impressões pesar
// tanto quanto uma de 10 mil.
export const ctrMedio = (campanhas) => {
  const impressoes = campanhas.reduce(
    (soma, c) => soma + (c.metrics?.impressions || 0),
    0
  );
  const cliques = campanhas.reduce(
    (soma, c) => soma + (c.metrics?.clicks || 0),
    0
  );

  return impressoes > 0 ? (cliques / impressoes) * 100 : 0;
};

// "Acima" ou "abaixo" da média da própria conta, com folga de 10% para não
// apontar diferença que é ruído.
export const compararCtr = (ctr, media) => {
  if (!media || !ctr) return null;

  if (ctr >= media * 1.1) return "acima";
  if (ctr <= media * 0.9) return "abaixo";

  return "media";
};

export const dinheiro = (valor, moeda = "BRL") => {
  if (valor === null || valor === undefined) return "—";

  return Number(valor).toLocaleString("pt-BR", {
    style: "currency",
    currency: moeda
  });
};

export const numero = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });

export const porcento = (valor) =>
  `${Number(valor || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`;

// Por que um anúncio não está rodando. A plataforma responde isso em três
// campos diferentes, e a tela do Facebook esconde os três atrás de um ícone.
export const problemaDe = (no) => {
  if (no?.campaignIssuesInfo?.errorSummary) {
    return no.campaignIssuesInfo.errorSummary;
  }

  if (no?.reviewStatus && /reject|disapprov/i.test(no.reviewStatus)) {
    return "Reprovado na revisão da plataforma";
  }

  const servindo = no?.servingStatuses || [];

  if (servindo.includes("CAMPAIGN_PAUSED")) return "A campanha está pausada";
  if (servindo.includes("ADSET_PAUSED")) return "O conjunto está pausado";
  if (servindo.includes("NO_ACTIVE_ADS")) return "Sem anúncio ativo";
  if (servindo.includes("ACCOUNT_DISABLED")) return "Conta de anúncios bloqueada";

  return null;
};

export const SITUACOES = {
  active: { rotulo: "Ativa", classe: "bg-emerald-500", texto: "text-emerald-600 dark:text-emerald-400" },
  paused: { rotulo: "Pausada", classe: "bg-amber-500", texto: "text-amber-600 dark:text-amber-400" },
  completed: { rotulo: "Concluída", classe: "bg-slate-400", texto: "text-muted-foreground" },
  error: { rotulo: "Com erro", classe: "bg-destructive", texto: "text-destructive" },
  rejected: { rotulo: "Reprovada", classe: "bg-destructive", texto: "text-destructive" },
  pending: { rotulo: "Em revisão", classe: "bg-sky-500", texto: "text-sky-600 dark:text-sky-400" }
};

export const situacaoDe = (valor) =>
  SITUACOES[valor] || {
    rotulo: valor || "—",
    classe: "bg-muted-foreground",
    texto: "text-muted-foreground"
  };
